/**
 * Sprint v1.7 Phase 3 (Do 03.09.2026) — A1 Anliegen-Credit-Counter.
 *
 * Free-User: Zeigt Zähler „X von 3 heute genutzt" plus visuelle Dots.
 *            Bei 3/3: klarer Sperr-Zustand mit Pro-Vorteile-Teaser (Conversion-Hook).
 * Pro-User:  Rendert NICHTS (unlimited, keine UI-Störung).
 *
 * Datenquelle: GET /api/anliegen/daily-usage
 * Refresh: `refreshKey`-Prop erhöhen (z.B. nach jedem Generate-Call).
 *
 * Sprachprinzip v8: Alltagssprache, keine konkreten Euro-Beträge
 * außer dem 39-€-Anker (Pricing-Disclosure).
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ArrowRight, Info, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { trackAnliegenLimitReached } from "@/lib/ga4";
import { useAuth } from "@/context/AuthContext";
import { pickProSoloCopy } from "@/lib/proTrialCopy";

export default function AnliegenCreditCounter({ refreshKey = 0, onLimitReached }) {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const copy = pickProSoloCopy(user);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/anliegen/daily-usage").then(
      ({ data }) => {
        if (cancelled) return;
        setUsage(data);
        setLoading(false);
        if (data && !data.unlimited && data.remaining === 0) {
          // Sprint V27 GA4 — Free-User hit Tageslimit (Upgrade-Trigger-Punkt)
          trackAnliegenLimitReached({
            used: data.used_today ?? 3, limit: data.limit ?? 3,
          });
          if (typeof onLimitReached === "function") onLimitReached();
        }
      },
      () => {
        if (!cancelled) setLoading(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [refreshKey, onLimitReached]);

  // Sprint V26 A5 (Fr 27.02.2026) — Cluster A5-Fix: Zeitangabe klarer
  // formulieren. Bisher zeigte der Reset-Text z.B. „ab morgen 02:00 Uhr",
  // was verwirrt (nachts). Neue Logik betont „morgen früh" und nennt die
  // Uhrzeit nur ergaenzend in Klammern. UTC/Berlin-Konvertierung bleibt.
  const resetAt = usage?.reset_at;
  const resetLabel = React.useMemo(() => {
    try {
      if (!resetAt) return "morgen früh wieder verfügbar";
      const reset = new Date(resetAt);
      const now = new Date();
      const diffMs = reset.getTime() - now.getTime();
      const hours = Math.max(0, Math.round(diffMs / (1000 * 60 * 60)));
      const localTime = reset.toLocaleTimeString("de-DE", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Europe/Berlin",
      });
      if (hours <= 1) return `in ca. 1 Stunde wieder verfügbar (${localTime} Uhr)`;
      if (hours < 6) return `in ca. ${hours} Stunden wieder verfügbar (${localTime} Uhr)`;
      return `morgen früh wieder verfügbar (ab ${localTime} Uhr)`;
    } catch {
      return "morgen früh wieder verfügbar";
    }
  }, [resetAt]);

  // Pro / Powerpaket / cancelled ohne Limit → nichts rendern
  if (loading || !usage || usage.unlimited) return null;

  const { used_today: used = 0, limit = 3, remaining = 3 } = usage;
  const isBlocked = remaining <= 0;
  const dots = Array.from({ length: limit }, (_, i) => i < used);

  if (isBlocked) {
    return (
      <section
        className="rounded-2xl border-2 border-[var(--fts-primary-bright)] bg-white p-5 shadow-sm"
        data-testid="anliegen-limit-reached"
        aria-live="polite"
      >
        <div className="flex items-start gap-3">
          <div
            className="w-10 h-10 rounded-xl bg-[var(--fts-primary-bright)]/10 text-[var(--fts-primary-bright)] flex items-center justify-center flex-shrink-0"
            aria-hidden="true"
          >
            <Lock className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
              Tageslimit erreicht
            </p>
            <h3
              className="mt-1 text-lg font-extrabold text-slate-900"
              data-testid="anliegen-limit-headline"
            >
              Du hast deine 3 Anliegen für heute genutzt.
            </h3>
            <p className="mt-2 text-sm text-slate-700 leading-relaxed" data-testid="anliegen-limit-reset-text">
              Neue Anliegen sind <b>{resetLabel}</b>. Für <b>Mustererkennung</b> über
              alle deine Anliegen hinweg und <b>personalisierte Empfehlungen</b> ohne
              Tageslimit gibt es Pro Solo.
            </p>
            <ul className="mt-3 space-y-1.5 text-sm text-slate-700">
              <li className="flex items-start gap-2">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[var(--fts-primary-bright)]" />
                Unbegrenzte Flexpfad-Anliegen
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[var(--fts-primary-bright)]" />
                Mustererkennung ab dem 7. Anliegen
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[var(--fts-primary-bright)]" />
                Personalisierte Empfehlungen basierend auf deinem Profil
              </li>
            </ul>
            <button
              type="button"
              onClick={() => navigate("/pricing")}
              data-testid="anliegen-limit-upgrade-cta"
              className="mt-4 inline-flex items-center gap-1.5 rounded-xl bg-[var(--fts-primary-bright)] hover:opacity-90 text-white font-bold text-sm px-4 py-2.5"
            >
              {copy.ctaLabel}
              <ArrowRight className="w-4 h-4" />
            </button>
            {copy.isTrialEligible && (
              <span
                data-testid="anliegen-limit-trial-badge"
                className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-wider align-middle"
              >
                <Sparkles className="w-3 h-3" /> 7 Tage · 1 €
              </span>
            )}
            <p className="mt-2 text-xs text-slate-500">
              {copy.priceLine}
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex items-center gap-3 flex-wrap"
      data-testid="anliegen-credit-counter"
      aria-label="Tageslimit für Free-Anliegen"
    >
      <div className="flex items-center gap-2">
        <Info
          className="w-4 h-4 text-[var(--fts-primary-bright)] flex-shrink-0"
          aria-hidden="true"
        />
        <span
          className="text-sm font-semibold text-slate-800"
          data-testid="anliegen-credit-counter-label"
        >
          {used} von {limit} Anliegen heute genutzt
        </span>
      </div>
      <div
        className="flex items-center gap-1.5"
        data-testid="anliegen-credit-counter-dots"
        aria-hidden="true"
      >
        {dots.map((consumed, i) => (
          <span
            key={i}
            className={[
              "h-2.5 w-2.5 rounded-full transition-colors",
              consumed
                ? "bg-[var(--fts-primary-bright)]"
                : "bg-slate-200 border border-slate-300",
            ].join(" ")}
          />
        ))}
      </div>
      <span className="text-xs text-slate-500 ml-auto">
        Für Mustererkennung und personalisierte Empfehlungen:{" "}
        <button
          type="button"
          onClick={() => navigate("/pricing")}
          data-testid="anliegen-credit-counter-upgrade-link"
          className="font-bold text-[var(--fts-primary-bright)] hover:underline"
        >
          Pro Solo
        </button>
      </span>
    </section>
  );
}
